/**
 * Birdie Time odds maths — a byte-for-byte port of engine/birdie/odds.py
 * (155-game-birdie). The paytable hash commits what the widget DISPLAYS
 * (2-dp values) together with the ppm weights that fix the exact multiplier.
 * Locked by birdie-vectors.json (Task 5.12).
 */
export const PATTERNS = ['HHH', 'HHM', 'HMH', 'MHH', 'HMM', 'MHM', 'MMH', 'MMM'] as const
export const OPTIONS = [
  'IN3',
  'IN2',
  'IN1',
  'IN0',
  'HHM',
  'HMH',
  'MHH',
  'HMM',
  'MHM',
  'MMH'
] as const
export type PatternKey = (typeof PATTERNS)[number]
export type OptionKey = (typeof OPTIONS)[number]

/** 94% base since 2026-09-21 (was 950000). A round carries its own rtp_ppm; this is the fallback and the expectation. */
export const RTP_PPM = 940000
/** Shortest repr of 1e6/940000 − 1 — the one canonical literal on every side (A57). */
export const MARGIN = 0.06382978723404253
const SCALE = 1_000_000
const P_MIN = 150000
const P_MAX = 850000

const holes = (pattern: string): number => pattern.split('').filter((c) => c === 'H').length

export function patternWeightsPpm(makeRatePpm: number): number[] {
  const p = Math.min(P_MAX, Math.max(P_MIN, Math.trunc(makeRatePpm))) / SCALE
  const scaled = PATTERNS.map((pattern) => {
    const h = holes(pattern)
    return Math.pow(p, h) * Math.pow(1 - p, 3 - h) * SCALE
  })
  const floors = scaled.map((w) => Math.floor(w))
  let remainder = SCALE - floors.reduce((a, b) => a + b, 0)
  // Largest remainder; ties broken by the lower index (PATTERNS order).
  const byRemainder = scaled
    .map((w, i) => ({ i, frac: w - Math.floor(w) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (let k = 0; remainder > 0 && k < byRemainder.length; k++, remainder--) {
    floors[byRemainder[k]!.i]! += 1
  }
  return floors
}

export function countPpm(weights: number[]): Record<'IN3' | 'IN2' | 'IN1' | 'IN0', number> {
  const sum = (...idx: number[]) => idx.reduce((a, i) => a + (weights[i] ?? 0), 0)
  return { IN3: sum(0), IN2: sum(1, 2, 3), IN1: sum(4, 5, 6), IN0: sum(7) }
}

export function multiplier(ppm: number): number {
  return 1 / ((ppm / SCALE) * (1 + MARGIN))
}

/** The 2-dp display term of the hash: floor(m*100 + 0.5) — never Math.round/banker’s. */
export function displayMultiplierCents(m: number): number {
  return Math.floor(m * 100 + 0.5)
}

export function paytableCanonical(
  rtpPpm: number,
  weights: number[],
  multipliersInOptionsOrder: number[]
): string {
  return `${rtpPpm}|${weights.join(',')}|${multipliersInOptionsOrder.map(displayMultiplierCents).join(',')}`
}

export async function paytableHash(
  rtpPpm: number,
  weights: number[],
  multipliersInOptionsOrder: number[]
): Promise<string> {
  const bytes = new TextEncoder().encode(
    paytableCanonical(rtpPpm, weights, multipliersInOptionsOrder)
  )
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

export function boardFromMakeRate(makeRatePpm: number): {
  weightsPpm: number[]
  multipliers: Record<OptionKey, number>
} {
  const weightsPpm = patternWeightsPpm(makeRatePpm)
  const counts = countPpm(weightsPpm)
  const byPattern = Object.fromEntries(PATTERNS.map((p, i) => [p, weightsPpm[i]!])) as Record<
    PatternKey,
    number
  >
  const multipliers = Object.fromEntries(
    OPTIONS.map((o) => [
      o,
      multiplier(o in counts ? counts[o as keyof typeof counts] : byPattern[o as PatternKey])
    ])
  ) as Record<OptionKey, number>
  return { weightsPpm, multipliers }
}
